/**
 * Tuning Presets Library
 * Built-in tuning tables for common tuning systems:
 * - Equal Divisions of the Octave (EDO)
 * - Just Intonation (JI)
 * - Wilson Combination Product Sets (CPS)
 * - Non-octave tunings (Bohlen-Pierce)
 */

import type { TuningTable } from '@/types/config';

export interface TuningPreset {
  name: string;
  category: string;
  description: string;
  tuningTable: TuningTable;
  suggestedIntervals: { q_cents: number; r_cents: number };
}

/**
 * Convert a ratio (num/den) to 0.1-cent units.
 */
function ratioToCentsX10(num: number, den: number): number {
  return Math.round(1200 * Math.log2(num / den) * 10);
}

/**
 * Generate an Equal Division of the Octave (or other period) tuning table.
 * @param divisions - Number of equal divisions
 * @param period_cents - Period in 0.1-cent units (default: 12000 = octave)
 */
export function generateEDO(divisions: number, period_cents: number = 12000): TuningTable {
  const pitches = Array.from({ length: divisions }, (_, i) => Math.round(i * period_cents / divisions));
  return {
    notes_per_period: divisions,
    period_is_octave: Math.abs(period_cents - 12000) < 10,
    period_cents,
    pitches,
    scale_mask: Array.from({ length: divisions }, () => true),
  };
}

/**
 * Compute Wicki-Hayden-style suggested intervals for N-EDO.
 * q-axis ~ whole tone (2/12 of octave), r-axis ~ fifth (7/12 of octave)
 */
function edoSuggestedIntervals(divisions: number, period_cents: number = 12000): { q_cents: number; r_cents: number } {
  const q_steps = Math.round(divisions * 2 / 12);
  const r_steps = Math.round(divisions * 7 / 12);
  return {
    q_cents: Math.round(q_steps * period_cents / divisions),
    r_cents: Math.round(r_steps * period_cents / divisions),
  };
}

/**
 * Build a TuningTable from an array of ratio pairs [num, den].
 * 1/1 is implicitly included as degree 0.
 */
function ratiosToTable(ratios: [number, number][]): TuningTable {
  const pitches = [0, ...ratios.map(([n, d]) => ratioToCentsX10(n, d))];
  const N = pitches.length;
  return {
    notes_per_period: N,
    period_is_octave: true,
    period_cents: 12000,
    pitches,
    scale_mask: Array.from({ length: N }, () => true),
  };
}

/**
 * Build a TuningTable from 0.1-cent pitch values (NOT including 0 for 1/1).
 */
function centsToTable(centsX10: number[], period_cents: number = 12000): TuningTable {
  const pitches = [0, ...centsX10];
  const N = pitches.length;
  return {
    notes_per_period: N,
    period_is_octave: Math.abs(period_cents - 12000) < 10,
    period_cents,
    pitches,
    scale_mask: Array.from({ length: N }, () => true),
  };
}

// --- EDO Presets ---

const edoDivisions = [5, 7, 10, 12, 15, 17, 19, 22, 24, 31, 41, 53, 72];

const edoPresets: TuningPreset[] = edoDivisions.map((n) => ({
  name: `${n}-EDO`,
  category: 'Equal Division',
  description: `${n} equal divisions of the octave`,
  tuningTable: generateEDO(n),
  suggestedIntervals: edoSuggestedIntervals(n),
}));

// --- Just Intonation Presets ---

const jiMajor7: TuningPreset = {
  name: 'JI Major (7-note)',
  category: 'Just Intonation',
  description: '5-limit just intonation major scale: 1/1 9/8 5/4 4/3 3/2 5/3 15/8',
  tuningTable: ratiosToTable([
    [9, 8], [5, 4], [4, 3], [3, 2], [5, 3], [15, 8],
  ]),
  suggestedIntervals: {
    q_cents: ratioToCentsX10(9, 8),  // whole tone ~204c
    r_cents: ratioToCentsX10(3, 2),  // fifth ~702c
  },
};

const jiMinor7: TuningPreset = {
  name: 'JI Minor (7-note)',
  category: 'Just Intonation',
  description: '5-limit just intonation minor scale: 1/1 9/8 6/5 4/3 3/2 8/5 9/5',
  tuningTable: ratiosToTable([
    [9, 8], [6, 5], [4, 3], [3, 2], [8, 5], [9, 5],
  ]),
  suggestedIntervals: {
    q_cents: ratioToCentsX10(9, 8),
    r_cents: ratioToCentsX10(3, 2),
  },
};

// Pythagorean 12-note: chain of fifths from Gb to B
const pythagorean12: TuningPreset = {
  name: 'Pythagorean (12-note)',
  category: 'Just Intonation',
  description: '3-limit Pythagorean tuning via chain of pure fifths',
  tuningTable: (() => {
    // Generate pitches from chain of fifths: Gb Db Ab Eb Bb F C G D A E B
    // Centered on C, then sorted ascending
    const fifthRatio = 3 / 2;
    // fifths from C: 0=C, 1=G, 2=D, 3=A, 4=E, 5=B, -1=F, -2=Bb, -3=Eb, -4=Ab, -5=Db, -6=Gb
    const fifthPositions = [-6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5];
    const rawCents = fifthPositions.map((pos) => {
      const ratio = Math.pow(fifthRatio, pos);
      let cents = 1200 * Math.log2(ratio);
      // Reduce to within octave [0, 1200)
      cents = ((cents % 1200) + 1200) % 1200;
      return Math.round(cents * 10);
    });
    rawCents.sort((a, b) => a - b);
    // First pitch should be 0 (unison)
    const pitches = rawCents;
    return {
      notes_per_period: 12,
      period_is_octave: true,
      period_cents: 12000,
      pitches,
      scale_mask: Array.from({ length: 12 }, () => true),
    };
  })(),
  suggestedIntervals: {
    q_cents: ratioToCentsX10(9, 8),  // Pythagorean whole tone
    r_cents: ratioToCentsX10(3, 2),  // pure fifth
  },
};

// --- Wilson CPS Presets ---

const hexany: TuningPreset = {
  name: 'Hexany {1,3,5,7}',
  category: 'Wilson CPS',
  description: '6-note combination product set from factors 1, 3, 5, 7',
  tuningTable: centsToTable([
    2669, // 266.9c (= 267 in 0.1c)
    4351, // 435.1c
    5825, // 582.5c
    7020, // 702.0c
    9688, // 968.8c
  ]),
  suggestedIntervals: {
    q_cents: 2669, // ~267c step
    r_cents: 7020, // ~702c fifth-like
  },
};

const dekany: TuningPreset = {
  name: 'Dekany {1,3,5,7,9}',
  category: 'Wilson CPS',
  description: '10-note combination product set from factors 1, 3, 5, 7, 9',
  tuningTable: centsToTable([
    // 2-out-of-5 CPS: all products of 2 factors from {1,3,5,7,9}
    // Products: 3,5,7,9,15,21,27,35,45,63 -> sorted by cents in octave
    2041, // 204.1c (9/8)
    2669, // 266.9c (7/6)
    3863, // 386.3c (5/4)
    4980, // 498.0c (4/3)
    5825, // 582.5c (7/5)
    7020, // 702.0c (3/2)
    7849, // 784.9c (63/40 ~ 8/5)
    9331, // 933.1c (12/7)
    9688, // 968.8c (7/4)
  ]),
  suggestedIntervals: {
    q_cents: 2041, // ~204c
    r_cents: 7020, // ~702c
  },
};

// --- Non-octave Presets ---

const bohlenPierce: TuningPreset = {
  name: 'Bohlen-Pierce (13-ED3)',
  category: 'Non-octave',
  description: '13 equal divisions of the tritave (3/1 = 1902 cents)',
  tuningTable: generateEDO(13, 19020),
  suggestedIntervals: (() => {
    // For BP: step = 19020/13 ≈ 1463 (0.1-cent)
    return {
      q_cents: Math.round(2 * 19020 / 13), // ~2 steps
      r_cents: Math.round(6 * 19020 / 13), // ~6 steps (close to 3/2 within tritave)
    };
  })(),
};

// --- Export all presets ---

export const tuningPresets: TuningPreset[] = [
  ...edoPresets,
  jiMajor7,
  jiMinor7,
  pythagorean12,
  hexany,
  dekany,
  bohlenPierce,
];
